import React, { useState, useEffect } from 'react';
import { FiSave, FiUpload, FiClock, FiCheckCircle, FiXCircle, FiX } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VendorSettings = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    homeDeliveryEnabled: false,
    pickupEnabled: false,
    vehicleEnabled: false,
    roomServiceEnabled: false,
    deliveryRadius: 5,
    deliveryCharge: 0,
    maxOrderPrice: 0,
    deliveryChargeType: 'fixed', // 'fixed' or 'dynamic'
    minimumOrderAmount: 300, // MOA
    deliveryAmount: 50, // DA
    homeDeliveryApprovedOnce: false,
    pickupApprovedOnce: false,
    vehicleApprovedOnce: false,
    roomServiceApprovedOnce: false,
    discount: {
      enabled: false,
      discountType: 'percentage',
      discountValue: 0,
      minOrderValue: 0,
      maxDiscount: 0
    }
  });
  const [upiQRCode, setUpiQRCode] = useState('');
  const [upiQRFile, setUpiQRFile] = useState(null);
  const [deliveryRequests, setDeliveryRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [pendingRequest, setPendingRequest] = useState(null);
  const [pendingToggleValue, setPendingToggleValue] = useState(null);
  const [currentSettingType, setCurrentSettingType] = useState('homeDelivery');
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState("");

  useEffect(() => {
    fetchCoupons();
    fetchSettings();
    fetchDeliveryRequests();
  }, []);

  useEffect(()=>{
    const coupon = coupons.find(c => c._id === settings?.discount?.couponId);
    setSelectedCoupon(coupon || null);
  },[settings])

  const fetchCoupons = async () => {
    try {
      const response = await api.get('/vendor/coupons');
      setCoupons(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      // setLoading(false);
    }
  };

  const handleCouponSelect = (couponId) => {
    const coupon = coupons.find(c => c._id === couponId);
    setSelectedCoupon(coupon || null);
    setSettings({
      ...settings,
      discount: {
        couponId: couponId,
        enabled: true,
        discountType: coupon.discountType,
        discountValue: coupon.discountValue,
        minOrderValue: coupon.minOrderValue,
        maxDiscount: coupon.maxDiscount
      }
    });
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/vendor/profile');
      const vendor = response.data.data;
      setSettings({
        homeDeliveryEnabled: vendor.settings?.homeDeliveryEnabled || false,
        pickupEnabled: vendor.settings?.pickupEnabled || false,
        vehicleEnabled: vendor.settings?.vehicleEnabled || false,
        roomServiceEnabled: vendor.settings?.roomServiceEnabled || false,
        deliveryRadius: vendor.settings?.deliveryRadius || 5,
        deliveryCharge: vendor.settings?.deliveryCharge || 0,
        maxOrderPrice: vendor.settings?.maxOrderPrice || 0,
        deliveryChargeType: vendor.settings?.deliveryChargeType || 'fixed',
        minimumOrderAmount: vendor.settings?.minimumOrderAmount || 300,
        deliveryAmount: vendor.settings?.deliveryAmount ?? 50,
        homeDeliveryApprovedOnce: vendor.settings?.homeDeliveryApprovedOnce || false,
        pickupApprovedOnce: vendor.settings?.pickupApprovedOnce || false,
        vehicleApprovedOnce: vendor.settings?.vehicleApprovedOnce || false,
        roomServiceApprovedOnce: vendor.settings?.roomServiceApprovedOnce || false,
        discount: {
          couponId: vendor.settings?.discount?.couponId,
          enabled: vendor.settings?.discount?.enabled || false,
          discountType: vendor.settings?.discount?.discountType || 'percentage',
          discountValue: vendor.settings?.discount?.discountValue || 0,
          minOrderValue: vendor.settings?.discount?.minOrderValue || 0,
          maxDiscount: vendor.settings?.discount?.maxDiscount || 0
        }
      });
      

      setUpiQRCode(vendor.upiQRCode || '');
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryRequests = async () => {
    try {
      const response = await api.get('/vendor/settings/delivery-requests');
      setDeliveryRequests(response.data.data);
      const pending = response.data.data.find(req => req.status === 'pending');
      setPendingRequest(pending || null);
    } catch (error) {
      console.error('Failed to fetch delivery requests');
    }
  };

  const handleServiceToggle = async (settingType, checked) => {
    if (pendingRequest && pendingRequest.settingType === settingType) {
      toast.error('You already have a pending request. Please wait for admin approval.');
      return;
    }

    const settingMap = {
      homeDelivery: { enabled: 'homeDeliveryEnabled', approved: 'homeDeliveryApprovedOnce', label: 'Home Delivery' },
      pickup: { enabled: 'pickupEnabled', approved: 'pickupApprovedOnce', label: 'Pickup' },
      vehicle: { enabled: 'vehicleEnabled', approved: 'vehicleApprovedOnce', label: 'Vehicle Service' },
      roomService: { enabled: 'roomServiceEnabled', approved: 'roomServiceApprovedOnce', label: 'Room Service' }
    };

    const setting = settingMap[settingType];

    // If vendor was approved once, allow direct toggle without modal
    if (settings[setting.approved]) {
      try {
        const response = await api.post('/vendor/settings/delivery-request', {
          settingType,
          enabled: checked,
          deliveryRadius: settingType === 'homeDelivery' ? settings.deliveryRadius : undefined,
          reason: ''
        });

        if (response.data.success) {
          setSettings({ ...settings, [setting.enabled]: checked });
          toast.success(`${setting.label} settings updated successfully`);
          fetchSettings();
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
      return;
    }

    // First time or after rejection - show modal for approval
    setCurrentSettingType(settingType);
    setPendingToggleValue(checked);
    setShowRequestModal(true);
  };

  const handleRequestSubmit = async () => {
    try {
      const response = await api.post('/vendor/settings/delivery-request', {
        settingType: currentSettingType,
        enabled: pendingToggleValue,
        deliveryRadius: currentSettingType === 'homeDelivery' ? settings.deliveryRadius : undefined,
        reason: requestReason
      });

      const settingMap = {
        homeDelivery: { enabled: 'homeDeliveryEnabled', label: 'Home Delivery' },
        pickup: { enabled: 'pickupEnabled', label: 'Pickup' },
        vehicle: { enabled: 'vehicleEnabled', label: 'Vehicle Service' },
        roomService: { enabled: 'roomServiceEnabled', label: 'Room Service' }
      };

      const setting = settingMap[currentSettingType];

      // Check if it was a direct update or request creation
      if (response.data.message?.includes('updated successfully')) {
        // Direct update - vendor was approved once
        toast.success(`${setting.label} settings updated successfully`);
        setSettings({ ...settings, [setting.enabled]: pendingToggleValue });
      } else {
        // Request created - needs approval
        toast.success('Request submitted successfully. Waiting for admin approval.');
        fetchDeliveryRequests();
      }
      
      setShowRequestModal(false);
      setRequestReason('');
      setPendingToggleValue(null);
      setCurrentSettingType('homeDelivery');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit request');
      setPendingToggleValue(null);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    
    try {
      await api.delete(`/vendor/settings/delivery-requests/${requestId}`);
      toast.success('Request cancelled successfully');
      fetchDeliveryRequests();
      setPendingRequest(null);
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await api.put('/vendor/settings', settings);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleUPIUpload = async (e) => {
    e.preventDefault();
    if (!upiQRFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        await api.post('/vendor/upi-qr', { upiQRCode: reader.result });
        toast.success('UPI QR code uploaded successfully');
        setUpiQRCode(reader.result);
        setUpiQRFile(null);
      };
      reader.readAsDataURL(upiQRFile);
    } catch (error) {
      toast.error('Failed to upload UPI QR code');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 p-4 md:p-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Manage your restaurant settings</p>
      </div>

      {/* Delivery & Pickup Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Delivery & Pickup Settings</h3>
        </div>
        <form onSubmit={handleSaveSettings} className="p-6">
          {pendingRequest && (
            <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
              <div className="flex items-center gap-3">
                <FiClock size={20} className="text-amber-600" />
                <div>
                  <strong className="text-amber-800">Pending Approval Request</strong>
                  <p className="text-sm text-amber-700 mt-1">
                    Request to {pendingRequest.requestedValue ? 'enable' : 'disable'} {pendingRequest.settingType === 'homeDelivery' ? 'home delivery' : pendingRequest.settingType === 'pickup' ? 'pickup' : pendingRequest.settingType === 'vehicle' ? 'vehicle service' : 'room service'} is awaiting super admin approval.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleCancelRequest(pendingRequest._id)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
              >
                Cancel Request
              </button>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.homeDeliveryEnabled}
                  onChange={(e) => handleServiceToggle('homeDelivery', e.target.checked)}
                  disabled={pendingRequest?.settingType === 'homeDelivery'}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-lg font-medium text-gray-900">Enable Home Delivery</span>
              </label>
              <p className="text-sm text-gray-600 mt-2 ml-8">
                Allow customers to order for home delivery (Requires super admin approval first time)
              </p>
            </div>

            {settings.homeDeliveryEnabled && (
              <div className="ml-8 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Radius (km)</label>
                  <input
                    type="text"
                    value={settings.deliveryRadius}
                    inputMode="decimal"
                    placeholder="Delivery radius (km)"
                    className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9.]/g, '');

                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }

                      setSettings({
                        ...settings,
                        deliveryRadius: value
                      });
                    }}
                  />
                  <p className="text-sm text-gray-600 mt-1">Maximum distance for delivery service</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Order Amount (₹)</label>
                  <input
                    type="text"
                    value={settings.minimumOrderAmount}
                    inputMode="decimal"
                    placeholder="Minimum order amount"
                    className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9.]/g, '');

                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }

                      setSettings({
                        ...settings,
                        minimumOrderAmount: value
                      });
                    }}
                  />
                  <p className="text-sm text-gray-600 mt-1">Base amount for delivery calculation</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Amount (₹)</label>
                  <input
                    type="text"
                    value={settings.deliveryAmount}
                    inputMode="decimal"
                    placeholder="Delivery charge"
                    className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9.]/g, '');

                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }

                      setSettings({
                        ...settings,
                        deliveryAmount: value
                      });
                    }}
                  />
                  <p className="text-sm text-gray-600 mt-1">Delivery charge for orders above minimum amount</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">How Delivery Charge Works:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• If order amount {'≤'} "Minimum Order Amount": Delivery charge = ("Minimum Order Amount" - order amount) + "Delivery Amount"</li>
                    <li>• If order amount {'>'} "Minimum Order Amount": Delivery charge = "Delivery Amount"</li>
                  </ul>
                </div>
              </div>
            )}

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.pickupEnabled}
                  onChange={(e) => handleServiceToggle('pickup', e.target.checked)}
                  disabled={pendingRequest?.settingType === 'pickup'}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-lg font-medium text-gray-900">Enable Pickup</span>
              </label>
              <p className="text-sm text-gray-600 mt-2 ml-8">
                Allow customers to place pickup orders (Requires super admin approval first time)
              </p>
            </div>

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.vehicleEnabled}
                  onChange={(e) => handleServiceToggle('vehicle', e.target.checked)}
                  disabled={pendingRequest?.settingType === 'vehicle'}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-lg font-medium text-gray-900">Enable Vehicle Service</span>
              </label>
              <p className="text-sm text-gray-600 mt-2 ml-8">
                Allow customers to order at vehicle parking (Requires super admin approval first time)
              </p>
            </div>
          </div>

          <div className="mt-8">
            <button type="submit" className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors duration-200">
              <FiSave /> Save Settings
            </button>
          </div>
        </form>
      </div>

      {/* Room Service Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Room Service Settings</h3>
        </div>
        <form onSubmit={handleSaveSettings} className="p-6">
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.roomServiceEnabled}
                onChange={(e) => handleServiceToggle('roomService', e.target.checked)}
                disabled={pendingRequest?.settingType === 'roomService'}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-lg font-medium text-gray-900">Enable Room Service</span>
            </label>
            <p className="text-sm text-gray-600 mt-2 ml-8">
              Allow room booking and management services (Requires super admin approval first time)
            </p>
          </div>

          <div className="mt-8">
            <button type="submit" className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors duration-200">
              <FiSave /> Save Room Service Settings
            </button>
          </div>
        </form>
      </div>

      {/* Discount Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Discount Settings</h3>
        </div>

        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Select the best coupon to apply globally on all orders
          </p>

          {/* Coupon dropdown */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Coupon *</label>
            <select
              value={settings.discount.couponId || ""}
              onChange={(e) => handleCouponSelect(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select Coupon --</option>
              {coupons.map(coupon => (
                <option key={coupon._id} value={coupon._id}>
                  {coupon.code} – {coupon.discountType === 'percentage'
                    ? `${coupon.discountValue}%`
                    : `₹${coupon.discountValue}`}
                </option>
              ))}
            </select>
          </div>

          {/* Selected coupon preview */}
          {selectedCoupon && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <strong className="text-gray-900">Applied Coupon</strong>
              <div className="mt-2 text-sm text-gray-700">
                <p><span className="font-medium">Code:</span> <span className="font-bold text-blue-600">{selectedCoupon.code}</span></p>
                <p><span className="font-medium">Discount:</span> {selectedCoupon.discountType === 'percentage'
                  ? `${selectedCoupon.discountValue}%`
                  : `₹${selectedCoupon.discountValue}`}</p>
                <p><span className="font-medium">Min Order:</span> ₹{selectedCoupon.minOrderValue || 0}</p>
                <p><span className="font-medium">Max Discount:</span> ₹{selectedCoupon.maxDiscount || 'No limit'}</p>
              </div>
            </div>
          )}

          <div className="mt-8">
            <button onClick={handleSaveSettings} className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors duration-200">
              <FiSave /> Save Discount Settings
            </button>
          </div>
        </div>
      </div>

      {/* UPI QR Code Upload */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">UPI QR Code</h3>
        </div>
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Upload your UPI QR code to be printed on customer bills
          </p>

          {upiQRCode && (
            <div className="mb-6">
              <p className="font-bold text-gray-900 mb-2">Current UPI QR Code:</p>
              <img 
                src={upiQRCode} 
                alt="UPI QR Code" 
                className="max-w-xs border border-gray-300 rounded-lg p-2"
              />
            </div>
          )}

          <form onSubmit={handleUPIUpload}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload New QR Code</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setUpiQRFile(e.target.files[0])}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-sm text-gray-600 mt-1">Accepted formats: JPG, PNG (Max 2MB)</p>
            </div>

            <button type="submit" className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200" disabled={!upiQRFile}>
              <FiUpload /> Upload QR Code
            </button>
          </form>
        </div>
      </div>

      {/* Restaurant Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Restaurant Information</h3>
        </div>
        <div className="p-6">
          <p className="text-gray-600">
            To update restaurant details, contact support or update from your profile section.
          </p>
        </div>
      </div>

      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Request Delivery Setting Change</h3>
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setRequestReason('');
                  setPendingToggleValue(null);
                }}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <FiX size={24} />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              You are requesting to <strong>{pendingToggleValue ? 'enable' : 'disable'}</strong> home delivery. 
              This change requires super admin approval.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Request (Optional)</label>
              <textarea
                rows="4"
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder="Explain why you want to make this change..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleRequestSubmit}
                className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors duration-200"
              >
                Submit Request
              </button>
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setRequestReason('');
                  setPendingToggleValue(null);
                }}
                className="flex-1 px-6 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorSettings;
